import { callLambda, type LambdaResultAction } from './lambda.js';
import type { StartableWorkflow } from '../models/company-workflow.js';

async function call(path: string, token: string | undefined, body: unknown): Promise<LambdaResultAction> {
  const result = await callLambda(path, 'POST', token, JSON.stringify(body));
  const { code, message } = result.res;
  if (code !== 200) throw new Error(typeof message === 'string' ? message : 'Something went wrong. Please try again.');
  return (Array.isArray(message) ? message[0] : message) as LambdaResultAction;
}

// Buttress refuses a lambda POST with an empty body, so the starting title always goes
export const createCompanyWorkflow = (token: string | undefined, title = 'New workflow') =>
  call('company-workflow/create', token, { title });

// A company workflow's draft: its roles, steps and endings, and the teams, people and data sets its roles are filled with
export const saveCompanyWorkflow = (
  token: string | undefined,
  companyWorkflowId: string,
  title: string,
  draft: unknown,
  config: unknown,
) => call('company-workflow/save', token, { companyWorkflowId, title, draft, config });

// What publishing a company workflow would refuse; it answers with the problems rather than refusing
export async function checkCompanyWorkflow(token: string | undefined, companyWorkflowId: string): Promise<string[]> {
  const result = await callLambda('company-workflow/check', 'POST', token, JSON.stringify({ companyWorkflowId }));
  const { code, message } = result.res;
  if (code !== 200) throw new Error(typeof message === 'string' ? message : 'Unable to check the workflow.');
  return (message as unknown as { problems: string[] }).problems;
}

export const publishCompanyWorkflow = (token: string | undefined, companyWorkflowId: string) =>
  call('company-workflow/publish', token, { companyWorkflowId });

export const disableCompanyWorkflow = (token: string | undefined, companyWorkflowId: string, disabled: boolean) =>
  call('company-workflow/disable', token, { companyWorkflowId, disabled });

export async function listStartableWorkflows(token: string | undefined): Promise<StartableWorkflow[]> {
  const result = await callLambda('company-workflow/startable', 'GET', token);
  const { code, message } = result.res;
  if (code !== 200) throw new Error(typeof message === 'string' ? message : 'Something went wrong. Please try again.');
  return Array.isArray(message) ? (message as unknown as StartableWorkflow[]) : [];
}

// Install's checks on a staff workflow being edited; staff only, and it answers with the problems rather than refusing them
export async function checkWorkflowSchema(
  token: string | undefined,
  schema: unknown,
): Promise<{ problems: string[]; warnings: string[] }> {
  const result = await callLambda('workflow/schema/check', 'POST', token, JSON.stringify(schema));
  const { code, message } = result.res;
  if (code !== 200) throw new Error(typeof message === 'string' ? message : 'Unable to check the workflow.');
  return message as unknown as { problems: string[]; warnings: string[] };
}
